import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { CheckboxInput } from '../../../../components/common/inputs/CheckboxInput';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { managedFarmCheckpointByTaskType, managedFarmFormSteps } from '../../../../constants/managedFarm';
import { managedFarmPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { managedFarmSlice } from '../../../../store/managedFarmSlice';
import {
  addNewTaskToList,
  createNewTask,
  getTaskListFromStorage,
  saveTaskListToStorage,
} from '../../../../utils/manager/managedFarmTaskManager.util';
import { ManagedFarmFormWrapper } from '../ManagedFarmFormWrapper';

export const ManagedFarmFormTaskCheckpointPage = () => {
  const navigate = useNavigate();

  const {
    address,
    coordinates,
    pnu,
    mapImage,
    landInfo,
    fieldOwnerName,
    fieldOwnerPhoneNumber,
    taskType,
    taskTypeEtc,
    equipment,
    equipmentEtc,
    cropType,
    cropTypeEtc,
    startDate,
    endDate,
  } = useAppSelector((state) => state.managedFarm);
  const dispatch = useAppDispatch();
  const { resetState } = managedFarmSlice.actions;

  const recommendedCheckpointsByTaskType = managedFarmCheckpointByTaskType[taskType];

  const [checkpoint, setCheckpoint] = useState<string>();
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<boolean[]>([]);

  const [newTaskId, setNewTaskId] = useState<string>();

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState<boolean>(false);

  const currentStep = managedFarmFormSteps.STEP4;
  const steps = Object.values(managedFarmFormSteps);

  const recommendedCheckpointToUse =
    recommendedCheckpointsByTaskType?.recommendedCheckpoint
      ?.filter((checkpoint, index) => {
        if (selectedCheckpoint[index] === true) {
          return true;
        }

        return false;
      })
      .join('\n') ?? '';

  const resetManagedFarmState = () => dispatch(resetState());

  const handleManagedFarmFormComplete = async () => {
    const newTask = createNewTask({
      address,
      coordinates,
      pnu,
      mapImage,
      landInfo,
      fieldOwnerName,
      fieldOwnerPhoneNumber,
      taskType,
      taskTypeEtc,
      equipment,
      equipmentEtc,
      cropType,
      cropTypeEtc,
      startDate,
      endDate,
      checkpoint: checkpoint ?? '',
      recommendedCheckpoints: recommendedCheckpointToUse,
      status: '작업 예정',
    });
    setNewTaskId(newTask.id);

    try {
      const savedTaskList = await getTaskListFromStorage();
      const newTaskList = addNewTaskToList(savedTaskList, newTask);

      await saveTaskListToStorage(newTaskList);

      ampli.pvmFinishManageFarmForm({
        check_point: checkpoint ?? '',
        recommended_check_point: recommendedCheckpointToUse.split('\n'),
      });

      ReactGA.event('pvm_finish_manage_farm_form', {
        check_point: checkpoint ?? '',
        recommended_check_point: recommendedCheckpointToUse.split('\n'),
      });

      setIsCompleteModalOpen(true);
    } catch (error) {
      console.error('태스크 저장 실패:', error);
    }
  };

  useEffect(() => {}, [taskType]);

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.formTaskCheckpointPage.title}</title>
      </Helmet>
      <ManagedFarmFormWrapper
        headerTitle={'필지 소유자에게 전달할 사항을\n입력하세요.'}
        canConfirm
        steps={steps}
        currentStep={currentStep}
        confirmHandler={handleManagedFarmFormComplete}
        confirmButtonText={'작업 저장하기'}
      >
        <SurveyInputSection>
          <TopSectionContianer>
            <HeaderWrapper>
              <SurveyInputHeader inputTitle={'전달할 사항'} isOptional />
              <Textarea
                value={checkpoint}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCheckpoint(e.target.value)}
                placeholder={recommendedCheckpointsByTaskType?.placeholder ?? ''}
              />
            </HeaderWrapper>
          </TopSectionContianer>
          <BottomSectionContainer>
            <HeaderWrapper>
              <SurveyInputHeader inputTitle={'추천 전달 사항'} />
              <BottomSectionDescriptionWrapper>
                <BottomSectionDescription>{'아래 사항을 선택하면 함께 입력되어요.'}</BottomSectionDescription>
              </BottomSectionDescriptionWrapper>
            </HeaderWrapper>
            {recommendedCheckpointsByTaskType?.recommendedCheckpoint &&
              recommendedCheckpointsByTaskType?.recommendedCheckpoint.length > 0 &&
              recommendedCheckpointsByTaskType.recommendedCheckpoint.map((checkpoint, index) => (
                <Checkbox
                  key={checkpoint}
                  id={checkpoint}
                  text={checkpoint}
                  isChecked={selectedCheckpoint[index]}
                  onChange={() =>
                    setSelectedCheckpoint((prev) => {
                      const newSelectedCheckpoint = [...prev];
                      newSelectedCheckpoint[index] = !newSelectedCheckpoint[index];
                      return newSelectedCheckpoint;
                    })
                  }
                />
              ))}
          </BottomSectionContainer>
        </SurveyInputSection>
      </ManagedFarmFormWrapper>

      <PopupConfirmModal
        isShow={isCompleteModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsCompleteModalOpen(false)}
        title={'작업 저장 완료 🎉'}
        description={'내 작업 관리에서 바로\n확인할 수 있어요!'}
        buttonArray={[
          {
            text: '상세 내용 보러가기',
            onClick: () => {
              resetManagedFarmState();
              navigate(`${managedFarmPageInfo.taskDetailPage.path}/${newTaskId}?from=form`, { replace: true });
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const Checkbox = ({
  id,
  text,
  isChecked,
  onChange,
}: {
  id: string;
  text: string;
  isChecked: boolean;
  onChange: () => void;
}) => {
  return (
    <CheckBoxBackground htmlFor={id}>
      <CheckBoxText>{text}</CheckBoxText>
      <CheckboxInput id={id} isChecked={isChecked} onChange={onChange} />
    </CheckBoxBackground>
  );
};

const CheckBoxBackground = styled.label`
  height: 72px;
  min-height: 72px;

  padding: 16px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const CheckBoxText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const TopSectionContianer = styled.div`
  display: flex;
  padding: 0px 16px 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 120px;
  min-height: 120px;

  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;

  padding: 10px 16px;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  ::placeholder {
    color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

    /* Body/Medium/m-regular */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */
  }
`;

const BottomSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 0px 16px 24px 16px;
`;

const BottomSectionDescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const BottomSectionDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;
